import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import { FindInPage as FindInPageIcon } from '@mui/icons-material';
import syscheckApiService from '../../services/syscheck/SyscheckApiService';
import dashboardApiService from '../../services/dashboard/DashboardApiService';

const SysScan = () => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetchingAgents, setFetchingAgents] = useState(true);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setFetchingAgents(true);
        const agentsData = await dashboardApiService.getAllAgents();
        setAgents(agentsData || []);
        if (agentsData && agentsData.length > 0) {
          setSelectedAgent(agentsData[0].id);
        }
      } catch (err) {
        setError('Failed to fetch agents');
        console.error('Error fetching agents:', err);
      } finally {
        setFetchingAgents(false);
      }
    };
    fetchAgents();
  }, []);

  const handleFetchResults = useCallback(async () => {
    if (!selectedAgent) return;
    try {
      setLoading(true);
      setError(null);
      setScanResult(null); // Clear previous results
      const result = await syscheckApiService.getSyscheckResult(selectedAgent);
      setScanResult(result);
    } catch (err) {
      setError('Failed to fetch scan results');
      console.error('Scan results fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedAgent]);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" fontWeight="bold">SysScan Results</Typography>
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>Agent Selection</Typography>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={9}>
              <FormControl fullWidth>
                <InputLabel>Select Agent</InputLabel>
                <Select
                  value={selectedAgent}
                  onChange={(e) => {
                    setSelectedAgent(e.target.value);
                    setScanResult(null); // Clear results when agent changes
                    setError(null);
                  }}
                  label="Select Agent"
                  disabled={fetchingAgents}
                >
                  {agents.map((agent) => (
                    <MenuItem key={agent.id} value={agent.id}>
                      {agent.name} ({agent.id})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={handleFetchResults}
                disabled={!selectedAgent || loading}
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <FindInPageIcon />}
              >
                Fetch Scan Results
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>}

      {scanResult && (
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight="bold">Scan Results for Agent {selectedAgent}</Typography>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>File Path</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Modified</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell>User/Group</TableCell>
                    <TableCell>SHA256 Hash</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scanResult.data && scanResult.data.affected_items.length > 0 ? (
                    scanResult.data.affected_items.map((item, index) => (
                      <TableRow key={item.file || index} hover>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{item.file}</TableCell>
                        <TableCell>{item.type}</TableCell>
                        <TableCell>{item.size}</TableCell>
                        <TableCell>{formatTimestamp(item.mtime)}</TableCell>
                        <TableCell>{item.perm}</TableCell>
                        <TableCell>{item.uname}/{item.gname}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>
                          <Tooltip title={item.sha256} arrow>
                            <span>{`${item.sha256.substring(0, 15)}...`}</span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">No scan results found for this agent.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default SysScan;
